<template>
    <v-card v-if="form" outlined class="mb-4 req-card" :dir="$vuetify.rtl ? 'rtl' : 'ltr'">
        <div class="sec-head"><v-icon small class="sec-ic">mdi-chart-line</v-icon><span>{{ $t('RequestDetails') }}</span></div>
        <div class="pa-5">
            <RequestSummaryHeader :title="form.requestTitle" :year="form.year" :period="form.period" />

            <div class="grp"><v-icon small class="grp-ic me-2">mdi-text-box-outline</v-icon>{{ $t('RequestDescription') }}</div>
            <div class="desc">{{ form.description || '—' }}</div>

            <template v-if="attachments.length">
                <div class="grp"><v-icon small class="grp-ic me-2">mdi-paperclip</v-icon>{{ $t('Attachments') }}</div>
                <div v-for="(a, i) in attachments" :key="i" class="att-row d-flex align-center">
                    <div class="att-ic"><v-icon small :color="canView(a) ? '#e53935' : '#90a0ad'">{{ fileIcon(a) }}</v-icon></div>
                    <span class="att-name">{{ fileName(a) }}</span>
                    <v-spacer />
                    <v-btn v-if="canView(a)" x-small depressed color="primary" class="ms-2 att-btn" @click="view(a)">
                        <v-icon x-small>mdi-eye-outline</v-icon><span>{{ $t('View') }}</span>
                    </v-btn>
                    <v-btn x-small outlined color="primary" class="ms-2 att-btn" @click="download(a)">
                        <v-icon x-small>mdi-download</v-icon><span>{{ $t('Download') }}</span>
                    </v-btn>
                </div>
            </template>
        </div>
    </v-card>
</template>

<script>
    import ProcessesService from '@/services/ProcessesService.js'
    import RequestSummaryHeader from '@/components/app/process/RequestSummaryHeader.vue'
    import { normalizeAttachments } from '@/helpers/attachments.js'

    export default {
        components: { RequestSummaryHeader },

        props: {
            taskId: { type: Number, default: null },
            processInstanceId: { type: Number, default: null }
        },
        data() {
            return { form: null, attachments: [] }
        },
        watch: {
            taskId: { immediate: true, handler() { this.load() } },
            processInstanceId: { immediate: true, handler() { this.load() } }
        },
        methods: {
            load() {
                const req = this.taskId
                    ? ProcessesService.getOperationalIndicatorRequestByTask(this.taskId)
                    : (this.processInstanceId ? ProcessesService.getOperationalIndicatorRequestByProcess(this.processInstanceId) : null)
                if (!req) return
                this.form = null
                this.attachments = []
                req.then(res => {
                    if (res.data) {
                        this.form = {
                            description: res.data.description,
                            requestTitle: res.data.requestTitle,
                            year: res.data.year,
                            period: res.data.period
                        }
                        this.attachments = normalizeAttachments(res.data)
                    }
                }).catch(() => { /* no linked inquiry request */ })
            },
            // a = { url: GUID storage path, name: filename the user uploaded }
            fileName(a) { return (a && a.name) || '' },
            canView(a) { return /\.pdf$/i.test((a && a.url) || '') },
            fileIcon(a) {
                const u = (a && a.url) || ''
                if (/\.pdf$/i.test(u)) return 'mdi-file-pdf-box'
                if (/\.(docx?|rtf)$/i.test(u)) return 'mdi-file-word-box'
                if (/\.xlsx?$/i.test(u)) return 'mdi-file-excel-box'
                return 'mdi-file-outline'
            },
            attachmentUrl(a, dl) {
                const token = this.$store.getters['user/token']
                return process.env.VUE_APP_MAIN_API + 'operational-indicator-requests/attachment'
                    + '?path=' + encodeURIComponent((a && a.url) || '')
                    + '&access_token=' + encodeURIComponent(token)
                    + (dl ? '&dl=true' : '')
            },
            view(a) {
                window.open(this.attachmentUrl(a, false), '_blank')
            },
            download(a) {
                // cross-origin, so the saved name comes from the API's Content-Disposition
                const link = document.createElement('a')
                link.href = this.attachmentUrl(a, true)
                link.setAttribute('download', this.fileName(a))
                document.body.appendChild(link)
                link.click()
                link.remove()
            }
        }
    }
</script>

<style scoped>
    .req-card { border: 1px solid #e6ecea !important; border-radius: 18px !important; overflow: hidden; box-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px -16px rgba(15, 23, 42, .18) !important; }
    .sec-head {
        display: flex; align-items: center; gap: 10px; font-weight: 800; font-size: 15px;
        padding: 14px 20px; color: #1f2a28; background: #fff; border-bottom: 1px solid #e6ecea;
    }
    .sec-head .sec-ic {
        width: 32px; height: 32px; border-radius: 10px; background: #e6f2f1; color: #0a716e !important;
        display: inline-flex; align-items: center; justify-content: center; flex: 0 0 32px;
    }
    .grp {
        display: flex; align-items: center; font-weight: 700; font-size: 13.5px; color: #1B8354;
        margin: 6px 0 12px; padding-bottom: 9px; border-bottom: 2px solid #eef4f1;
    }
    .grp:not(:first-of-type) { margin-top: 22px; }
    .grp-ic { color: #1B8354 !important; }
    .desc { font-size: 13.5px; color: #22323d; line-height: 1.8; white-space: pre-wrap; }
    .att-row { background: #f7f9fb; border: 1px solid #edf1f5; border-radius: 10px; padding: 9px 15px; margin-bottom: 8px; }
    .att-ic {
        width: 30px; height: 30px; border-radius: 7px; background: #fff; border: 1px solid #edf1f5;
        display: flex; align-items: center; justify-content: center; margin-inline-end: 10px; flex: 0 0 30px;
    }
    .att-name { font-size: 13px; color: #37474f; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .att-btn { border-radius: 8px; font-weight: 600; }
    .att-btn ::v-deep .v-btn__content { gap: 5px; }
</style>
